// API 基础配置
const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8787'

import type { AuthResponse, User, Student, AnalysisTask, LearningReport, Quota, ApiConfig } from '../types'

// 通用请求封装
async function request<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<T> {
  const token = localStorage.getItem('token')

  const headers: HeadersInit = {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...options.headers,
  }

  const response = await fetch(`${API_BASE_URL}${endpoint}`, {
    ...options,
    headers,
  })

  const data = await response.json()

  if (!response.ok) {
    throw new Error(data.error || data.message || 'Request failed')
  }

  return data
}

// ========== 认证 API ==========
export const authApi = {
  register: (data: { email: string; password: string; displayName?: string; region?: string }) =>
    request<AuthResponse>('/api/auth/register', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  login: (data: { email: string; password: string }) =>
    request<AuthResponse>('/api/auth/login', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  me: () => request<User>('/api/auth/me'),
}

// ========== 学生 API ==========
export const studentsApi = {
  getAll: () => request<{ students: Student[]; total: number }>('/api/students').then(res => res.students),

  getOne: (id: string) => request<Student>(`/api/students/${id}`),

  create: (data: Omit<Student, 'id' | 'teacherId' | 'createdAt' | 'updatedAt'>) =>
    request<Student>('/api/students', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  update: (id: string, data: Partial<Student>) =>
    request<Student>(`/api/students/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),

  delete: (id: string) =>
    request<void>(`/api/students/${id}`, {
      method: 'DELETE',
    }),
}

// ========== 上传 API ==========
export const uploadApi = {
  uploadImage: async (file: File, studentId: string): Promise<{ id: string }> => {
    // 将文件转换为 base64
    const base64Data = await new Promise<string>((resolve, reject) => {
      const reader = new FileReader()
      reader.onload = () => {
        const result = reader.result as string
        resolve(result)
      }
      reader.onerror = reject
      reader.readAsDataURL(file)
    })

    const response = await request<{ imageIds: string[] }>('/api/upload', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        studentId,
        files: [{
          fileName: file.name,
          fileSize: file.size,
          base64Data,
        }],
      }),
    })

    return { id: response.imageIds[0] }
  },
}

// ========== 分析任务 API ==========
export const analysisApi = {
  create: (data: { studentId: string; imageIds: string[] }) =>
    request<{ taskId: string; status: string }>('/api/analysis', {
      method: 'POST',
      body: JSON.stringify(data),
    }).then(res => ({ 
      id: res.taskId, 
      studentId: data.studentId, 
      imageIds: data.imageIds, 
      status: res.status as any,
      progress: 0,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    } as AnalysisTask)),

  getTasks: () => request<{ tasks: AnalysisTask[]; total: number }>('/api/analysis').then(res => res.tasks),

  getTask: (id: string) => request<AnalysisTask>(`/api/analysis/${id}`),

  cancel: (id: string) =>
    request<void>(`/api/analysis/${id}`, {
      method: 'DELETE',
    }),
}

// ========== 报告 API ==========
export const reportsApi = {
  getAll: () => request<{ reports: LearningReport[]; total: number }>('/api/reports').then(res => res.reports),

  getOne: (id: string) => request<LearningReport>(`/api/reports/${id}`),

  getByStudent: (studentId: string) => request<LearningReport[]>(`/api/reports?studentId=${studentId}`),
}

// ========== 设置 API ==========
export const settingsApi = {
  getQuota: () => request<Quota>('/api/settings/quota'),

  getApiKeys: () => request<ApiConfig[]>('/api/settings/apikeys'),

  createApiKey: (data: Omit<ApiConfig, 'id' | 'teacherId' | 'createdAt'>) =>
    request<ApiConfig>('/api/settings/apikeys', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  updateApiKey: (id: string, data: Partial<ApiConfig>) =>
    request<ApiConfig>(`/api/settings/apikeys/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),

  deleteApiKey: (id: string) =>
    request<void>(`/api/settings/apikeys/${id}`, {
      method: 'DELETE',
    }),
}

// ========== 文件访问 ==========
export const getFileUrl = (r2Key: string): string => {
  return `${API_BASE_URL}/api/files/${encodeURIComponent(r2Key)}`
}
